import { useState } from "react";
import { Mail, MapPin, Phone, Clock, ArrowRight } from "lucide-react";
import { toast, Toaster } from "sonner";
import { useLanguage } from "../contexts/LanguageContext";
import { submitContact } from "../lib/api";

const initial = {
  name: "",
  company: "",
  email: "",
  phone: "",
  service: "",
  message: "",
};

const Contact = () => {
  const { t, lang } = useLanguage();
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitContact({ ...form, lang });
      toast.success(t.contact.success);
      setForm(initial);
    } catch (err) {
      const msg = err?.response?.data?.detail || t.contact.error;
      toast.error(typeof msg === "string" ? msg : t.contact.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Toaster richColors position="top-center" />

      <section className="pt-20 pb-12 md:pt-28 md:pb-16">
        <div className="container-rotam grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <p className="overline">{t.contact.overline}</p>
            <h1 className="mt-4 font-heading font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              {t.contact.title}
            </h1>
          </div>
          <div className="lg:col-span-5">
            <p className="text-rotam-muted leading-relaxed text-base md:text-lg">
              {t.contact.description}
            </p>
          </div>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="container-rotam grid lg:grid-cols-12 gap-12">
          {/* Contact info */}
          <aside className="lg:col-span-4 space-y-10">
            <div data-testid="office-info">
              <h3 className="overline text-rotam-accent">{t.contact.officeTitle}</h3>
              <div className="mt-4 space-y-4 text-sm">
                <p className="flex items-start gap-3 text-rotam-ink">
                  <MapPin size={16} className="mt-0.5 text-rotam-primary shrink-0" />
                  {t.contact.address}
                </p>
                <p className="flex items-center gap-3 text-rotam-ink">
                  <Phone size={16} className="text-rotam-primary shrink-0" />
                  <a href={`tel:${t.contact.phoneNumber.replace(/\s/g, "")}`} className="hover:text-rotam-primary">
                    {t.contact.phoneNumber}
                  </a>
                </p>
                <p className="flex items-center gap-3 text-rotam-ink">
                  <Mail size={16} className="text-rotam-primary shrink-0" />
                  <a href={`mailto:${t.contact.emailAddress}`} className="hover:text-rotam-primary">
                    {t.contact.emailAddress}
                  </a>
                </p>
              </div>
            </div>

            <div data-testid="working-hours">
              <h3 className="overline text-rotam-accent">{t.contact.hoursTitle}</h3>
              <p className="mt-4 flex items-center gap-3 text-sm text-rotam-ink">
                <Clock size={16} className="text-rotam-primary" />
                {t.contact.hours}
              </p>
            </div>

            <div className="bg-white border border-rotam-border p-8 rounded-sm">
              <p className="text-sm text-rotam-muted leading-relaxed">
                {lang === "tr"
                  ? "Ön görüşmemiz tamamen ücretsizdir. Şirketinizin mevcut yapısını inceleyip yol haritası öneririz."
                  : "Our initial consultation is completely free. We review your company's current setup and propose a roadmap."}
              </p>
            </div>
          </aside>

          {/* Form */}
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="lg:col-span-8 bg-white border border-rotam-border p-8 md:p-12 rounded-sm"
          >
            <div className="grid sm:grid-cols-2 gap-8">
              <div>
                <label className="text-xs tracking-[0.18em] uppercase text-rotam-muted">
                  {t.contact.name} *
                </label>
                <input
                  required
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  data-testid="contact-name"
                  className="input-line mt-2"
                />
              </div>
              <div>
                <label className="text-xs tracking-[0.18em] uppercase text-rotam-muted">
                  {t.contact.company}
                </label>
                <input
                  name="company"
                  value={form.company}
                  onChange={onChange}
                  data-testid="contact-company"
                  className="input-line mt-2"
                />
              </div>
              <div>
                <label className="text-xs tracking-[0.18em] uppercase text-rotam-muted">
                  {t.contact.email} *
                </label>
                <input
                  required
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  data-testid="contact-email"
                  className="input-line mt-2"
                />
              </div>
              <div>
                <label className="text-xs tracking-[0.18em] uppercase text-rotam-muted">
                  {t.contact.phone}
                </label>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  data-testid="contact-phone"
                  className="input-line mt-2"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs tracking-[0.18em] uppercase text-rotam-muted">
                  {t.contact.service}
                </label>
                <select
                  name="service"
                  value={form.service}
                  onChange={onChange}
                  data-testid="contact-service"
                  className="input-line mt-2 cursor-pointer"
                >
                  <option value="">{t.contact.selectService}</option>
                  {t.services.items.map((s) => (
                    <option key={s.key} value={s.key}>
                      {s.title}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs tracking-[0.18em] uppercase text-rotam-muted">
                  {t.contact.message} *
                </label>
                <textarea
                  required
                  rows={5}
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  data-testid="contact-message"
                  className="input-line mt-2 resize-none"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit"
              className="mt-10 btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? t.contact.submitting : t.contact.submit}
              <ArrowRight size={16} />
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Contact;
